import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Link2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Badge, Button } from './ui';
import type { CrmLookupResult, CrmSystem, EnrichedLink } from '../types';

const SYSTEM_LABEL: Record<CrmSystem, string> = {
  MANDANT: 'Mandant',
  BEWERBER: 'Bewerber',
  LEAD: 'Lead',
};

/**
 * CRM-Seitenleiste: zeigt die verknüpften CRM-Systeme (mit Live-Daten aus
 * /enriched) und schlägt offene Treffer aus /crm-matches zum Zuordnen vor.
 *
 * Hinweis zum „Zuordnen": Das Backend bietet aktuell keinen Endpoint zum
 * manuellen Verlinken eines einzelnen Treffers. Stattdessen triggert der Button
 * /api/crm/auto-enrich — der durchsucht alle Contacts OHNE Links nach Email-/
 * Phone-Treffern und legt die Links idempotent an. Für diesen Contact heißt das:
 * solange er noch keine Verknüpfung hat, werden seine Treffer automatisch
 * verlinkt. Sobald ein manueller Link-Endpoint existiert, hier umstellen.
 */
export function ConversationCrmSidebar({ contactId }: { contactId: string }) {
  const queryClient = useQueryClient();

  const { data: enriched, isLoading: enrichedLoading } = useQuery({
    queryKey: ['contact-enriched', contactId],
    queryFn: () => api.get<{ items: EnrichedLink[] }>(`/api/contacts/${contactId}/enriched`),
    enabled: Boolean(contactId),
  });

  const { data: matchesData } = useQuery({
    queryKey: ['contact-crm-matches', contactId],
    queryFn: () => api.get<{ matches: CrmLookupResult[] }>(`/api/contacts/${contactId}/crm-matches`),
    enabled: Boolean(contactId),
  });

  const autoEnrich = useMutation({
    mutationFn: () => api.post<{ scanned: number; linksCreated: number }>('/api/crm/auto-enrich', {}),
    onSuccess: (res) => {
      if (res.linksCreated > 0) {
        toast.success(`${res.linksCreated} Verknüpfung(en) angelegt`);
      } else {
        toast.message('Keine neuen Verknüpfungen gefunden');
      }
      queryClient.invalidateQueries({ queryKey: ['contact-enriched', contactId] });
      queryClient.invalidateQueries({ queryKey: ['contact-crm-matches', contactId] });
      queryClient.invalidateQueries({ queryKey: ['conversation'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const links = enriched?.items ?? [];
  const linkedIds = new Set(links.map((l) => `${l.link.system}:${l.link.externalId}`));
  // Nur Treffer anzeigen, die noch nicht verknüpft sind.
  const openMatches = (matchesData?.matches ?? []).filter(
    (m) => !linkedIds.has(`${m.system}:${m.externalId}`),
  );

  return (
    <div className="mb-4">
      <div className="kalkuel-eyebrow">CRM</div>

      {enrichedLoading ? (
        <div className="mt-2 text-xs text-kalkuel-muted">Laden…</div>
      ) : links.length > 0 ? (
        <ul className="mt-2 space-y-2">
          {links.map((l) => {
            const href = l.data?.deepLink ?? l.link.externalRef ?? null;
            return (
              <li
                key={l.link.id}
                className="rounded-md border border-kalkuel-border bg-kalkuel-surface p-2.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    <Badge tone="accent">{SYSTEM_LABEL[l.link.system]}</Badge>
                    <span className="text-xs font-medium text-kalkuel-primary">
                      {l.data?.displayName ?? l.link.externalId}
                    </span>
                  </span>
                  {href && (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 text-kalkuel-accent2 hover:text-kalkuel-accent"
                      title="Im CRM öffnen"
                    >
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
                {l.data && <AttributeList attributes={l.data.attributes} />}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="mt-2 text-xs text-kalkuel-muted">Keine CRM-Verknüpfung.</div>
      )}

      {openMatches.length > 0 && (
        <div className="mt-3">
          <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-kalkuel-muted">
            Zuordnen
          </div>
          <ul className="space-y-2">
            {openMatches.map((m) => (
              <li
                key={`${m.system}:${m.externalId}`}
                className="rounded-md border border-dashed border-kalkuel-border p-2.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    <Badge tone="neutral">{SYSTEM_LABEL[m.system]}</Badge>
                    <span className="text-xs font-medium text-kalkuel-primary">{m.displayName}</span>
                  </span>
                </div>
                {(m.email || m.phone) && (
                  <div className="mt-1 text-[11px] text-kalkuel-muted">
                    {m.email ?? m.phone}
                  </div>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-2 w-full"
                  onClick={() => autoEnrich.mutate()}
                  disabled={autoEnrich.isPending}
                >
                  <Link2 size={11} />
                  Zuordnen
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {links.length === 0 && openMatches.length === 0 && (
        <Button
          size="sm"
          variant="ghost"
          className="mt-2"
          onClick={() => autoEnrich.mutate()}
          disabled={autoEnrich.isPending}
        >
          <Sparkles size={11} />
          CRM-Abgleich starten
        </Button>
      )}
    </div>
  );
}

function AttributeList({ attributes }: { attributes: Record<string, unknown> }) {
  const entries = Object.entries(attributes)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .slice(0, 6);
  if (entries.length === 0) return null;
  return (
    <dl className="mt-1.5 space-y-0.5">
      {entries.map(([key, value]) => (
        <div key={key} className="flex justify-between gap-2 text-[11px]">
          <dt className="text-kalkuel-muted">{key}</dt>
          <dd className="truncate text-right text-kalkuel-primary">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}
